import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { formatDuration } from '@/lib/format/duration';
import type { TikTokClipBlock } from '@/lib/publishing/resolveTikTokClipBlock';
import type { TikTokClipPreviewState } from '@/hooks/use-tiktok-clip-preview';

/** The video that will be sent to TikTok, playable before the user posts. */
export const TikTokClipPreview: React.FC<{ preview: TikTokClipPreviewState }> = ({ preview }) => {
  const { t } = useTranslation();
  if (!preview.videoUrl) return null;
  return (
    <div className="space-y-1.5">
      <p className="text-xs font-medium">{t('publishing.tiktok.previewLabel')}</p>
      <video
        data-testid="tiktok-clip-preview"
        src={preview.videoUrl}
        poster={preview.posterUrl || undefined}
        controls
        playsInline
        preload="metadata"
        onLoadedMetadata={(event) => preview.onDuration(event.currentTarget.duration)}
        className="mx-auto max-h-64 w-auto max-w-full rounded-md bg-black"
      />
    </div>
  );
};

/** Explains why the clip cannot be posted to TikTok as it is. */
export const TikTokClipBlockNotice: React.FC<{ block: TikTokClipBlock | null }> = ({ block }) => {
  const { t } = useTranslation();
  if (!block) return null;
  if (block.kind === 'watermarked') {
    return (
      <div
        role="alert"
        data-testid="tiktok-clip-watermarked"
        className="space-y-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm"
      >
        <p>{t('publishing.tiktok.watermarked')}</p>
        <Button asChild size="sm" variant="outline">
          <Link to="/plans">{t('publishing.tiktok.watermarkUpgrade')}</Link>
        </Button>
      </div>
    );
  }
  return (
    <p
      role="alert"
      data-testid="tiktok-clip-too-long"
      className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm"
    >
      {t('publishing.tiktok.tooLong', {
        duration: formatDuration(block.durationSeconds),
        max: formatDuration(block.maxDurationSeconds),
      })}
    </p>
  );
};
